/* ------------------------------------------------------------------------------------------------
   hh-rum.js v2.2 - field measurement from real visitors: INP with attribution, the REAL lead-photo
   paint, LCP and CLS. 260918 (v1, GA4) -> 260919 (v2, own endpoint).

   WHY THIS EXISTS
   Core Web Vitals fails on INP alone: 203 ms against a 200 ms threshold. The lab cannot settle a
   3 ms question - on one and the same build, session INP swings threefold between runs. Only the
   field can say whether a change helped, and only if the data are collected BEFORE and AFTER it.

   WHERE IT SENDS
   One beacon per page view, as the page goes away, to the store's own App Proxy
   (/apps/hh/api/rum - the same path the cross-sell beacons use), which the engine worker writes into
   Cloudflare Analytics Engine. Not GA4, on purpose: GA4 folds high-cardinality fields (target,
   script) into "(other)", needs custom dimensions registered and 24-48 h before anything shows, and
   only sees consenting visitors. Nothing here identifies anyone: the request carries no cookie
   (credentials: omit), no id is minted, and the worker stores no IP.

   WHAT IT MEASURES
   inp_ms          the page view's INP interaction - the worst one; from 50 interactions on, one more
                   is skipped per 50, capped at 10 (Chrome's own definition). 0 = there were
                   interactions but none reached 16 ms. null = no interaction at all.
   delay/processing/presentation   the split of that interaction, over ALL its Event Timing entries that ended
                   at the same paint (v2.1; v2.0 read the longest entry alone and hid click-handler work in
                   "presentation" - rows before 2026-09-19 carry that flaw in these two fields, INP itself is fine).
   script*         the Long Animation Frame script that held the frame around it (host+path, function,
                   duration, of which forced style & layout).
   photo_ms        Element Timing of the lead product photo (elementtiming="hh-lead"). The attribute is
                   also set from here for markup that lacks it, which works whenever the photo paints
                   after this script ran - always true for a lazy photo, usually true for an eager one.
   lcp_ms          the last LCP candidate. Today that is the invisible marker (Pavel: it stays), which
                   is exactly why photo_ms exists; lcp_ms is kept so a broken marker would show.
   cls             session windows as web-vitals does them: 1 s gap, 5 s cap, max window wins.

   WHEN IT SENDS NOTHING
   Theme previews, xs-preview, the theme editor and automated browsers - so our own tests never land
   in the dataset. Browsers without Event Timing interactionId (Safari, Firefox) - the same population
   CrUX cannot see either.

   COST
   Four PerformanceObservers and one callback as the page goes away. No timers, no polling, nothing on
   the interaction path itself. Kill switch: delete the one <script> line in theme.liquid.
   ------------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  var THRESHOLD = 16;     // Event Timing durationThreshold, its minimum - so fast page views count too
  var LOAF_KEEP = 40;     // rolling window of Long Animation Frames kept for attribution

  /* Read HERE, at evaluation: document.currentScript is null by the time a callback runs. */
  var tag = document.currentScript || document.querySelector('script[src*="hh-rum"]');
  var ENDPOINT = (tag && tag.getAttribute('data-rum')) || '/apps/hh/api/rum';

  /* Which kind of page. From Liquid via data-tpl - this theme puts no template-* class on <body>. */
  var TPL = (function () {
    try {
      var t = tag && tag.getAttribute('data-tpl');
      if (t) return t.split('.')[0].slice(0, 40);
      var m = location.pathname.match(/\/(products|collections|pages|blogs|search|cart)(\/|$)/);
      return m ? m[1] : 'index';
    } catch (e) { return 'other'; }
  })();

  try {
    if (!window.PerformanceObserver || !PerformanceObserver.supportedEntryTypes) return;
    if (PerformanceObserver.supportedEntryTypes.indexOf('event') < 0) return;
    if (!('interactionId' in PerformanceEventTiming.prototype)) return;   // no interactionId, no INP
    if (navigator.webdriver) return;                                       // our own headless rigs
    if (window.Shopify && window.Shopify.designMode) return;              // theme editor
    if (/[?&](preview_theme_id|xs-preview)=/.test(location.search)) return;   // draft previews, tests
  } catch (e) { return; }

  /* the lead photo: make sure Element Timing can see it even on markup without the attribute */
  try {
    var lead = document.querySelector('.hh-gal--lead img');
    if (lead && !lead.hasAttribute('elementtiming')) lead.setAttribute('elementtiming', 'hh-lead');
  } catch (e) {}

  /* ---- per-page-view state; a bfcache restore starts a new page view ---- */
  var byInteraction, ownCount, countBase, loafs, sent, navType, photoMs, lcpMs;
  var clsValue, sessValue, sessStart, sessLast;

  function fresh(nav) {
    byInteraction = {}; ownCount = 0; loafs = []; sent = false; navType = nav;
    photoMs = null; lcpMs = null; clsValue = 0; sessValue = 0; sessStart = 0; sessLast = 0;
    try { countBase = performance.interactionCount || 0; } catch (e) { countBase = 0; }
  }
  fresh((function () {
    try {
      if (document.prerendering) return 'prerender';
      var n = performance.getEntriesByType('navigation')[0];
      return n && n.type ? n.type : 'navigate';
    } catch (e) { return 'navigate'; }
  })());

  /* generated ids and classes (ProductSubmitButton-template--31128157913428__main, is-3) would
     shatter a group-by into hundreds of unique values; digits go, the shape stays */
  function ident(s) { return String(s).replace(/\d+/g, ''); }

  function selectorFor(el) {
    try {
      if (!el || !el.tagName) return '';
      var s = el.tagName.toLowerCase();
      if (el.id) return (s + '#' + ident(el.id)).slice(0, 100);
      var cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).slice(0, 3).map(ident).join('.') : '';
      if (cls) s += '.' + cls;
      var p = el.parentElement;
      if (p && !cls) {                 /* a bare tag says nothing: name the nearest identifiable parent */
        var pc = typeof p.className === 'string' ? p.className.trim().split(/\s+/)[0] : '';
        if (p.id) s = p.tagName.toLowerCase() + '#' + ident(p.id) + ' > ' + s;
        else if (pc) s = p.tagName.toLowerCase() + '.' + ident(pc) + ' > ' + s;
      }
      return s.slice(0, 100);
    } catch (e) { return ''; }
  }

  function observe(type, fn, extra) {
    try {
      var o = new PerformanceObserver(function (list) { list.getEntries().forEach(fn); });
      var opts = { type: type, buffered: true };
      if (extra) for (var k in extra) opts[k] = extra[k];
      o.observe(opts);
    } catch (e) { /* an unsupported type must never break the page */ }
  }

  observe('event', function (e) {
    if (!e.interactionId) return;
    var g = byInteraction[e.interactionId];
    if (!g) { g = byInteraction[e.interactionId] = { duration: 0, entries: [] }; ownCount++; }
    /* keep the timings of EVERY entry (no element references): a tap is pointerdown + pointerup + click under one
       interactionId, and only their union says who did the work - see split() */
    g.entries.push({ startTime: e.startTime, duration: e.duration, processingStart: e.processingStart, processingEnd: e.processingEnd });
    if (e.duration > g.duration) { g.duration = e.duration; g.startTime = e.startTime; g.name = e.name; g.target = selectorFor(e.target); }
  }, { durationThreshold: THRESHOLD });

  /* The phase split of an interaction, over ALL its entries that ended at the same paint as the longest one.
     v2.0 read it off the longest entry alone. For a tap that is pointerup: it starts before click and ends at
     the same frame, so it always wins - but the work happened in the CLICK handler, and seen from the pointerup
     entry that work is "presentation". The engine session caught it on day one: 91 % of pointerup rows had
     processing 0, and the same button reported 206 ms processing as click and 0 as pointerup. Same maths as
     web-vitals' attribution build: input delay to the first handler, processing from the first handler's start to
     the last handler's end, presentation from there to the paint. Entries painted in an earlier frame (a
     pointerdown that got its own frame) are left out; durations are 8 ms-granular, hence the tolerance. */
  function split(g) {
    var paint = g.startTime + g.duration, start = g.startTime, ps = Infinity, pe = -Infinity;
    g.entries.forEach(function (e) {
      if (Math.abs(e.startTime + e.duration - paint) > 8) return;
      if (e.startTime < start) start = e.startTime;
      if (e.processingStart < ps) ps = e.processingStart;
      if (e.processingEnd > pe) pe = e.processingEnd;
    });
    if (ps === Infinity) { ps = g.startTime; pe = g.startTime; }
    /* duration is 8 ms-granular but processingEnd is not, so paint can land a few ms BEFORE the last handler
       ended and presentation would come out negative - which the server stores as "not measured" and the
       row silently leaves the presentation percentile. Clamp at zero (v2.1.1). */
    return { inputDelay: Math.max(0, ps - start), processing: Math.max(0, pe - ps), presentation: Math.max(0, paint - pe) };
  }

  observe('long-animation-frame', function (l) {
    loafs.push(l);
    if (loafs.length > LOAF_KEEP) loafs.shift();
  });

  observe('element', function (e) {
    if (e.identifier === 'hh-lead' && photoMs == null) photoMs = e.renderTime || e.loadTime || e.startTime;
  });

  /* LCP candidates stop after the first input; the last one seen is the page's LCP */
  observe('largest-contentful-paint', function (e) { lcpMs = e.renderTime || e.loadTime || e.startTime; });

  observe('layout-shift', function (e) {
    if (e.hadRecentInput) return;
    if (sessValue && e.startTime - sessLast < 1000 && e.startTime - sessStart < 5000) {
      sessValue += e.value; sessLast = e.startTime;
    } else {
      sessValue = e.value; sessStart = e.startTime; sessLast = e.startTime;
    }
    if (sessValue > clsValue) clsValue = sessValue;
  });

  function interactionCount() {
    try { if (performance.interactionCount) return performance.interactionCount - countBase; } catch (e) {}
    return ownCount;
  }

  /* the INP interaction: worst first, then skip one per 50 interactions, at most 10 */
  function inpInteraction() {
    var all = [];
    for (var k in byInteraction) all.push(byInteraction[k]);
    if (!all.length) return null;
    all.sort(function (a, b) { return b.duration - a.duration; });
    return all[Math.min(all.length - 1, Math.min(9, Math.floor(interactionCount() / 50)))];
  }

  /* which script held the frame that interaction was waiting on */
  function blame(it) {
    var lo = it.startTime - 50, hi = it.startTime + it.duration + 50, best = null;
    loafs.forEach(function (l) {
      if (l.startTime + l.duration < lo || l.startTime > hi) return;
      (l.scripts || []).forEach(function (s) { if (!best || s.duration > best.duration) best = s; });
    });
    if (!best) return {};
    var src = best.sourceURL || best.invoker || '';
    /* host + path only, and the theme version folder normalised: cache busters and /t/22/ would
       shatter the group-by */
    try { var u = new URL(src); src = u.host + u.pathname; } catch (e) { src = String(src).split('?')[0]; }
    src = src.replace(/\/t\/\d+\//, '/t/_/');
    /* an inline script reports the page's own URL - one value per product handle; name it instead */
    try { if (src === location.host + location.pathname) src = '(inline:' + TPL + ')'; } catch (e) {}
    return {
      source: src.slice(0, 90),
      /* no function name (inline, anonymous): the invoker says what ran it, e.g. TIMER or BUTTON.x.onclick */
      fn: ident(String(best.sourceFunctionName || best.invoker || '')).slice(0, 60),
      ms: Math.round(best.duration || 0),
      forced: Math.round(best.forcedStyleAndLayoutDuration || 0)
    };
  }

  function num(v) { return (typeof v === 'number' && isFinite(v)) ? Math.round(v) : null; }

  function report() {
    if (sent) return;
    sent = true;
    try {
      var it = inpInteraction(), s = it ? blame(it) : {}, ph = it ? split(it) : {};
      var count = interactionCount();
      var p = {
        v: 2,                 /* 2 = phase split over all same-frame entries + clamp (v2.1.1+); the server keeps 1 too */
        tpl: TPL,
        market: (window.Shopify && window.Shopify.country) || null,
        theme: (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) ? String(window.Shopify.theme.id) : null,
        nav: navType || null,
        conn: (navigator.connection && navigator.connection.effectiveType) || null,
        dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
        vw: window.innerWidth || null,
        inp_ms: it ? num(it.duration) : (count > 0 ? 0 : null),
        delay_ms: it ? num(ph.inputDelay) : null,
        processing_ms: it ? num(ph.processing) : null,
        presentation_ms: it ? num(ph.presentation) : null,
        type: it ? it.name : null,
        target: it ? it.target : null,
        script: s.source || null,
        script_fn: s.fn || null,
        script_ms: it ? (s.ms || 0) : null,
        script_forced: it ? (s.forced || 0) : null,
        interactions: count,
        at_ms: it ? num(it.startTime) : null,
        photo_ms: num(photoMs),
        lcp_ms: num(lcpMs),
        cls: (typeof clsValue === 'number') ? Math.round(clsValue * 1000) / 1000 : null
      };
      /* keepalive: the page is going away; omit: no cookie leaves with it - same-origin would send them */
      fetch(ENDPOINT, {
        method: 'POST', keepalive: true, credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(p)
      }).catch(function () {});
    } catch (e) { /* telemetry must never break the page */ }
  }

  /* pagehide is the reliable one; visibilitychange catches the tab being switched away on phones,
     where pagehide may never come because the page goes into the back/forward cache instead */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') report();
  });
  window.addEventListener('pagehide', report);
  window.addEventListener('pageshow', function (e) { if (e.persisted) fresh('bfcache'); });
})();
